import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { X, Books, Sparkle, Plus } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { libraryToExercise } from "./constants";

// Category chip tints (dark surface + colored text on the palette drawer bg).
const CATEGORY_COLOR = {
  strength:     "text-orange-300",
  power:        "text-amber-300",
  olympic:      "text-red-300",
  plyometric:   "text-yellow-300",
  speed:        "text-cyan-300",
  conditioning: "text-green-300",
  mobility:     "text-purple-300",
  warmup:       "text-lime-300",
  activation:   "text-lime-300",
  accessory:    "text-blue-300",
  core:         "text-sky-300",
};

const CATEGORY_ORDER = [
  "strength", "power", "olympic", "plyometric", "speed", "conditioning",
  "mobility", "warmup", "activation", "accessory", "core",
];

/** Right-side drawer to browse the shared exercise library while building a
 *  program. Rows are draggable onto any session (via the MIME
 *  `application/x-edge-library-exercise`) and can also be added to a day via
 *  the picker button — mirroring the FavoritesPalette UX. */
export function LibraryPalette({ open, onClose, onAddToDay, days, currentWeek }) {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (category) params.set("category", category);
      params.set("limit", "200");
      const { data: list } = await api.get(`/exercise-library?${params.toString()}`);
      setRows(list.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [q, category]);

  const loadFilters = useCallback(async () => {
    try {
      const { data } = await api.get("/exercise-library/filters");
      setFilters(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (open) { load(); loadFilters(); } }, [open, load, loadFilters]);

  const currentWeekDays = (days || []).filter((d) => d.week === currentWeek && !d.is_rest);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div
      className="pointer-events-auto fixed inset-y-0 right-0 z-[100] w-[380px] bg-surface-DEFAULT border-l border-white/15 shadow-2xl flex flex-col"
      data-testid="library-palette"
    >
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="label-tiny text-brand-accent">// LIBRARY</div>
          <div className="font-heading text-lg flex items-center gap-1.5">
            <Books size={16} weight="duotone" className="text-brand-accent" />
            Exercise Palette
          </div>
        </div>
        <button onClick={onClose} data-testid="library-close" className="text-text-secondary hover:text-white"><X size={16} /></button>
      </div>

      <div className="p-3 border-b border-white/10 space-y-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, muscle, tag..."
          className="bg-surface-elevated border-white/15 h-9 text-sm"
          data-testid="library-search"
        />
        <div className="flex flex-wrap gap-1" data-testid="library-category-filter">
          <button
            onClick={() => setCategory("")}
            data-testid="library-cat-all"
            className={`text-[10px] label-tiny px-2 py-1 border transition ${category === "" ? "!bg-brand-accent text-slate-900 !border-brand-accent" : "border-white/15 text-white/80 hover:!border-brand-accent"}`}
          >ALL</button>
          {CATEGORY_ORDER.map((c) => {
            const n = filters?.categories?.[c] || 0;
            if (n === 0) return null;
            const active = category === c;
            return (
              <button
                key={c}
                onClick={() => setCategory(active ? "" : c)}
                data-testid={`library-cat-${c}`}
                className={`text-[10px] label-tiny px-2 py-1 border transition ${active ? "!bg-brand-accent text-slate-900 !border-brand-accent" : "border-white/15 text-white/80 hover:!border-brand-accent"}`}
              >{c.toUpperCase()} <span className="opacity-70 ml-0.5">{n}</span></button>
            );
          })}
        </div>
        <div className="text-[9px] label-tiny text-text-secondary">TIP: DRAG A ROW ONTO ANY SESSION</div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="library-list">
        {loading ? (
          <div className="p-4 text-xs text-text-secondary">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-center text-xs text-text-secondary" data-testid="library-empty">
            No exercises match this filter.
          </div>
        ) : rows.map((ex) => (
          <LibraryRow
            key={ex.exercise_id}
            ex={ex}
            days={currentWeekDays}
            onAdd={(dayKey) => {
              const [w, d] = dayKey.split("-").map(Number);
              onAddToDay?.(w, d, libraryToExercise(ex));
              toast.success(`Added "${ex.name}" to W${w}·D${d}`);
            }}
          />
        ))}
      </div>
      <div className="p-2 border-t border-white/10 text-[10px] text-text-secondary text-center">
        {rows.length} exercise{rows.length === 1 ? "" : "s"}
      </div>
    </div>,
    document.body
  );
}

function LibraryRow({ ex, days, onAdd }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const catColor = CATEGORY_COLOR[ex.category] || "text-white/70";

  const onDragStart = (e) => {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("application/x-edge-library-exercise", JSON.stringify(ex));
    // Fallback text (dropping into external targets shows just the name)
    e.dataTransfer.setData("text/plain", ex.name);
  };

  return (
    <div
      className="border border-white/10 bg-white/[0.02] p-2 cursor-grab active:cursor-grabbing hover:border-brand-accent/40 transition"
      draggable
      onDragStart={onDragStart}
      data-testid={`library-row-${ex.exercise_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[9px] label-tiny ${catColor}`}>{ex.category.toUpperCase()}</span>
            {ex.is_custom && <Sparkle size={9} weight="fill" className="text-brand-accent" />}
          </div>
          <div className="text-sm font-heading mt-0.5 truncate text-white">{ex.name}</div>
          <div className="text-[10px] text-text-secondary truncate">
            {ex.primary_muscle || "—"}
            {ex.equipment?.length > 0 ? ` · ${ex.equipment.slice(0, 2).join(", ")}` : ""}
            {ex.difficulty ? ` · ${ex.difficulty}` : ""}
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setPickerOpen((v) => !v)}
            className="p-1 border border-white/15 text-white/80 hover:!border-brand-accent hover:!text-brand-accent transition"
            data-testid={`library-add-${ex.exercise_id}`}
            title="Add to day"
          >
            <Plus size={11} weight="bold" />
          </button>
          {pickerOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-20 bg-surface-DEFAULT border border-white/15 shadow-2xl min-w-[140px]"
              data-testid={`library-picker-${ex.exercise_id}`}
            >
              {days.length === 0 ? (
                <div className="p-2 text-[10px] text-text-secondary">No non-rest days in this week</div>
              ) : days.map((d) => (
                <button
                  key={`${d.week}-${d.day}`}
                  onClick={() => { onAdd(`${d.week}-${d.day}`); setPickerOpen(false); }}
                  data-testid={`library-picker-day-${ex.exercise_id}-${d.week}-${d.day}`}
                  className="w-full text-left px-3 py-1.5 text-xs text-white/85 hover:bg-brand-accent hover:text-slate-900 transition"
                >
                  W{d.week}·D{d.day}{d.name ? ` · ${d.name}` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
